/**
 * IngredientsSection - Custom element for managing ingredient tabs
 */
class IngredientsSection extends HTMLElement {
  constructor() {
    super();

    this.buttons = [];
    this.panels = [];
    this.currentTab = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get all tab buttons and panels
    this.buttons = Array.from(
        this.querySelectorAll('.ingredients-tabs__button'),
    );
    this.panels = Array.from(this.querySelectorAll('.ingredients-tabs__panel'));

    if (this.buttons.length === 0 || this.panels.length === 0) {
      console.warn('ProductIngredientsTabs: No buttons or panels found');
      return;
    }

    // Set initial active tab
    this.currentTab = this.buttons.find(
        (btn) => btn.getAttribute('aria-selected') === 'true',
    );

    if (!this.currentTab && this.buttons.length > 0) {
      this.currentTab = this.buttons[0];
      this.currentTab.setAttribute('aria-selected', 'true');
    }

    // Add event listeners to all buttons
    this.buttons.forEach((button) => {
      button.addEventListener('click', (e) => this.handleTabClick(e));
    });

    // Add keyboard navigation
    this.addEventListener('keydown', (e) => this.handleKeyDown(e));

    // Show initial panel
    this.showPanel(this.currentTab.dataset.tab);

    // Destroy all inactive panel carousels first
    this.destroyInactivePanelCarousels(this.currentTab.dataset.tab);

    // Initialize carousel for the first active tab
    this.initPanelCarousel(this.currentTab.dataset.tab);
  }

  handleTabClick(event) {
    const button = event.currentTarget;
    const tabId = button.dataset.tab;

    if (button === this.currentTab) {
      return; // Already active
    }

    this.switchTab(button, tabId);
  }

  switchTab(button, tabId) {
    // Deactivate all buttons
    this.buttons.forEach((btn) => {
      btn.setAttribute('aria-selected', 'false');
    });

    // Activate clicked button
    button.setAttribute('aria-selected', 'true');
    this.currentTab = button;

    // Destroy carousels in all inactive panels
    this.destroyInactivePanelCarousels(tabId);

    // Show corresponding panel
    this.showPanel(tabId);

    // Initialize carousel in the active panel
    this.initPanelCarousel(tabId);

    // Dispatch custom event
    this.dispatchEvent(
        new CustomEvent('tab-changed', {
          detail: { tabId },
          bubbles: true,
        }),
    );
  }

  destroyInactivePanelCarousels(activeTabId) {
    // Find all panels that are NOT the active one
    this.panels.forEach((panel) => {
      if (panel.dataset.panel !== activeTabId) {
        const carousel = panel.querySelector('ingredients-carousel');
        if (carousel && typeof carousel.destroyCarousel === 'function') {
          carousel.destroyCarousel();
        }
      }
    });
  }

  initPanelCarousel(tabId) {
    const activePanel = this.panels.find(
        (panel) => panel.dataset.panel === tabId,
    );

    if (!activePanel) return;

    const carousel = activePanel.querySelector('ingredients-carousel');

    if (carousel && typeof carousel.initCarousel === 'function') {
      // Always reinitialize - destroy first if needed
      if (typeof carousel.isInitialized === 'function' && carousel.isInitialized()) {
        carousel.destroyCarousel();
      }
      // Small delay to ensure panel is visible before initializing carousel
      setTimeout(() => {
        carousel.initCarousel();
      }, 50);
    }
  }

  showPanel(tabId) {
    // Hide all panels
    this.panels.forEach((panel) => {
      panel.setAttribute('hidden', '');
      panel.setAttribute('aria-hidden', 'true');
    });

    // Show selected panel with delay for animation
    const activePanel = this.panels.find(
        (panel) => panel.dataset.panel === tabId,
    );

    if (activePanel) {
      // Remove hidden attribute to trigger CSS transition
      activePanel.removeAttribute('hidden');
      activePanel.setAttribute('aria-hidden', 'false');
    }
  }

  handleKeyDown(event) {
    const currentIndex = this.buttons.indexOf(this.currentTab);
    let targetButton = null;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        targetButton =
            this.buttons[currentIndex - 1] ||
            this.buttons[this.buttons.length - 1];
        break;

      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        targetButton = this.buttons[currentIndex + 1] || this.buttons[0];
        break;

      case 'Home':
        event.preventDefault();
        targetButton = this.buttons[0];
        break;

      case 'End':
        event.preventDefault();
        targetButton = this.buttons[this.buttons.length - 1];
        break;

      default:
        return;
    }

    if (targetButton) {
      targetButton.focus();
      this.switchTab(targetButton, targetButton.dataset.tab);
    }
  }

  /**
   * Public method to switch to a specific tab
   * @param {string} tabId - The ID of the tab to switch to
   */
  switchToTab(tabId) {
    const button = this.buttons.find((btn) => btn.dataset.tab === tabId);

    if (button) {
      this.switchTab(button, tabId);
    } else {
      console.warn(`ProductIngredientsTabs: Tab with ID "${tabId}" not found`);
    }
  }

  /**
   * Public method to get the current active tab ID
   * @returns {string|null} The current tab ID
   */
  getCurrentTab() {
    return this.currentTab ? this.currentTab.dataset.tab : null;
  }
}

/**
 * IngredientsCarousel - Custom element for ingredient carousel with Swiper
 */
class IngredientsCarousel extends HTMLElement {
  constructor() {
    super();

    this.swiper = null;
    this.initialized = false;
    this.contentElement = null;
    this.contentUpdateTimeout = null;
    this.slideCount = 0;
    this.animationFrames = new Map(); // Track animation frames for each slide
    this.isAnimating = false; // Flag to prevent progress event from overriding animation
    this.autoplayInterval = null;
  }

  connectedCallback() {
    // Check if this carousel is in an active panel
    const panel = this.closest('.ingredients-tabs__panel');
    const isActive = panel && !panel.hasAttribute('hidden');

    // Only initialize if in active panel
    if (isActive) {
      this.initCarousel();
    }
  }

  /**
   * Public method to initialize carousel
   */
  initCarousel() {
    if (this.initialized) {
      return; // Already initialized
    }

    // Wait for Swiper library to load
    if (typeof Swiper !== 'undefined') {
      this.init();
    } else {
      // If Swiper not loaded yet, wait for it
      this.waitForSwiper();
    }
  }

  /**
   * Check if carousel is initialized
   * @returns {boolean}
   */
  isInitialized() {
    return this.initialized;
  }

  waitForSwiper() {
    const checkSwiper = setInterval(() => {
      if (typeof Swiper !== 'undefined') {
        clearInterval(checkSwiper);
        this.init();
      }
    }, 100);

    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkSwiper);
      if (typeof Swiper === 'undefined') {
        console.error('IngredientsCarousel: Swiper library not loaded');
      }
    }, 5000);
  }

  /**
   * Get responsive values based on screen width
   */
  getResponsiveValues() {
    const width = window.innerWidth;

    if (width < 550) {
      // Mobile - calculate relative to screen width
      return {
        translateXMultiplier: width * 0.065, // ~25 at 550px
        translateYPower: 1,
        translateYMultiplier: 80,
      };
    } else if (width < 750) {
      // Small tablet - calculate relative to screen width
      return {
        translateXMultiplier: width * 0.18, // ~80 at 750px
        translateYPower: 1.2,
        translateYMultiplier: 80,
      };
    } else if (width < 990) {
      // Tablet - calculate relative to screen width
      return {
        translateXMultiplier: width * 0.999, // ~225 at 990px
        translateYPower: 1.2,
        translateYMultiplier: 90,
      };
    } else if (width < 1120) {
      // Tablet - calculate relative to screen width
      return {
        translateXMultiplier: width * 1.011, // ~225 at 990px
        translateYPower: 2.8,
        translateYMultiplier: 140,
      };
    } else if (width < 1440) {
      // Tablet - calculate relative to screen width
      return {
        translateXMultiplier: width * 1.011, // ~225 at 990px
        translateYPower: 2.8,
        translateYMultiplier: 140,
      };
    } else {
      // Desktop - fixed values
      return {
        translateXMultiplier: 300,
        translateYPower: 2.8,
        translateYMultiplier: 200,
      };
    }
  }

  /**
   * Animate slide to target position with smooth radial motion
   */
  animateSlideToPosition(slide, targetProgress, duration) {
    const slideIndex = Array.from(slide.parentNode.children).indexOf(slide);

    // Cancel any existing animation for this slide
    if (this.animationFrames.has(slideIndex)) {
      cancelAnimationFrame(this.animationFrames.get(slideIndex));
    }

    // Get current transform values
    const currentTransform =
        slide.style.transform || 'translate3d(0px, 0px, 0px) scale(1)';
    const currentMatch = currentTransform.match(
        /translate3d\(([^,]+)px,\s*([^,]+)px,.*scale\(([^)]+)\)/,
    );

    const startX = currentMatch ? parseFloat(currentMatch[1]) : 0;
    const startY = currentMatch ? parseFloat(currentMatch[2]) : 0;
    const startScale = currentMatch ? parseFloat(currentMatch[3]) : 1;

    const currentFilter = slide.style.filter || 'blur(0px)';
    const currentBlurMatch = currentFilter.match(/blur\(([^)]+)px\)/);
    const startBlur = currentBlurMatch ? parseFloat(currentBlurMatch[1]) : 0;

    // Get responsive values
    const responsive = this.getResponsiveValues();

    // Calculate target values
    const targetOffset = Math.abs(targetProgress);
    const targetX = targetProgress * responsive.translateXMultiplier;
    const targetY =
        Math.pow(targetOffset, responsive.translateYPower) *
        responsive.translateYMultiplier;
    const targetScale = 1 - Math.min(targetOffset * 0.55, 0.55);
    const targetBlur = targetOffset * 6;
    const targetZIndex = 1000 - Math.round(targetOffset * 100);

    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out-cubic)
      const eased = 1 - Math.pow(1 - progress, 3);

      // Interpolate values
      const currentX = startX + (targetX - startX) * eased;
      const currentY = startY + (targetY - startY) * eased;
      const currentScale = startScale + (targetScale - startScale) * eased;
      const currentBlur = startBlur + (targetBlur - startBlur) * eased;

      // Apply styles
      slide.style.transform = `translate3d(${currentX}px, ${currentY}px, 0) scale(${currentScale})`;
      slide.style.filter = `blur(${currentBlur}px)`;
      slide.style.zIndex = targetZIndex;

      if (progress < 1) {
        const frameId = requestAnimationFrame(animate);
        this.animationFrames.set(slideIndex, frameId);
      } else {
        this.animationFrames.delete(slideIndex);
      }
    };

    const frameId = requestAnimationFrame(animate);
    this.animationFrames.set(slideIndex, frameId);
  }

  init() {
    const swiperContainer = this.querySelector('.swiper');

    if (!swiperContainer) {
      console.warn('IngredientsCarousel: Swiper container not found');
      return;
    }

    // Count slides to determine if loop mode is possible
    this.slideCount = this.querySelectorAll('.swiper-slide').length;

    // Get or create content container
    this.contentElement = this.querySelector(
        '.ingredients-carousel__shared-content',
    );

    if (!this.contentElement) {
      // Create shared content container if doesn't exist
      this.contentElement = document.createElement('div');
      this.contentElement.className = 'ingredients-carousel__shared-content';
      this.appendChild(this.contentElement);
    }

    // Get autoplay interval from data attribute
    const intervalData = this.dataset.autoplayInterval;
    const interval = intervalData ? parseInt(intervalData, 10) : 0;

    if (interval > 0) {
      this.autoplayInterval = interval;
    }

    // Create Swiper config based on slide count
    const visibleSlides = 1.5;

    // Always enable loop, but configure properly for small slide counts
    const loopConfig =
        this.slideCount < 3
            ? {
              loop: true,
              loopAdditionalSlides: 3,
              loopedSlides: this.slideCount,
            }
            : {
              loop: true,
              loopedSlides: this.slideCount,
            };

    const swiperConfig = {
      slidesPerView: visibleSlides,
      centeredSlides: true,
      spaceBetween: 0,
      ...loopConfig,
      slidesPerGroup: 1,
      grabCursor: true,
      slideToClickedSlide: true,
      watchSlidesProgress: true,
      speed: 600,
      ...(this.autoplayInterval && {
        autoplay: {
          delay: this.autoplayInterval,
          disableOnInteraction: false,
        },
      }),
      on: {
        init: (swiper) => {
          const responsive = this.getResponsiveValues();

          swiper.slides.forEach((slide) => {
            slide.style.willChange = 'transform, filter';

            const progress = slide.progress;
            const offset = Math.abs(progress);
            const translateX = progress * responsive.translateXMultiplier;
            const translateY =
                Math.pow(offset, responsive.translateYPower) *
                responsive.translateYMultiplier;
            const scale = 1 - Math.min(offset * 0.45, 0.45);
            const opacity = 1;
            const blur = offset * 6;

            slide.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
            slide.style.filter = `blur(${blur}px)`;
            slide.style.opacity = opacity;
            slide.style.zIndex = 1000 - Math.round(offset * 100);
          });
        },
        progress: (swiper) => {
          // Skip if custom animation is running
          if (this.isAnimating) return;

          const responsive = this.getResponsiveValues();

          for (let i = 0; i < swiper.slides.length; i++) {
            const slide = swiper.slides[i];
            const progress = slide.progress;
            const offset = Math.abs(progress);
            const translateX = progress * responsive.translateXMultiplier;
            const translateY =
                Math.pow(offset, responsive.translateYPower) *
                responsive.translateYMultiplier;
            const scale = 1 - Math.min(offset * 0.55, 0.55);
            const opacity = 1;
            const blur = offset * 6;

            slide.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
            slide.style.filter = `blur(${blur}px)`;
            slide.style.opacity = opacity;
            slide.style.zIndex = 1000 - Math.round(offset * 100);
          }
        },
        slideChangeTransitionStart: (swiper) => {
          // Only use custom animation when NOT dragging (autoplay, clicks, keyboard)
          if (!swiper.touching) {
            // Block progress updates and start custom animation
            this.isAnimating = true;

            swiper.slides.forEach((slide, index) => {
              // slide.style.transitionDuration = '0ms';
              // if (index === swiper.activeIndex) {
              // this.animateSlideToPosition(
              //   slide,
              //   slide.progress,
              //   swiper.params.speed,
              // );
              // }
            });
          }
        },
        setTransition: (swiper, duration) => {
          swiper.slides.forEach((slide) => {
            slide.style.transition = `transform ${duration}ms ease, filter ${duration}ms ease, opacity ${duration}ms ease`;
          });
        },
        transitionEnd: () => {
          // Re-enable progress updates after animation completes
          this.isAnimating = false;
        },
        slideChange: (swiper) => {
          this.updateContent(swiper);
        },
        slideChangeTransitionEnd: (swiper) => {
          this.updateContent(swiper);
        },
      },
    };

    // Initialize Swiper
    this.swiper = new Swiper(swiperContainer, swiperConfig);
    this.initialized = true;

    // Set initial content
    this.updateContent(this.swiper);

    // Pause autoplay on hover
    if (this.autoplayInterval && this.swiper.autoplay) {
      this.addEventListener('mouseenter', () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.stop();
        }
      });
      this.addEventListener('mouseleave', () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.start();
        }
      });
    }

    // Dispatch custom event when carousel is ready
    this.dispatchEvent(
        new CustomEvent('carousel-ready', {
          detail: { swiper: this.swiper },
          bubbles: true,
        }),
    );
  }

  updateContent(swiper) {
    if (!this.contentElement) {
      return;
    }

    // Get the active slide element (works with loop)
    const activeSlide = swiper.slides[swiper.activeIndex];

    if (!activeSlide) {
      return;
    }

    const content = activeSlide.querySelector('.ingredient-card__content');

    if (!content) {
      return;
    }

    const title =
        content.querySelector('.ingredient-card__title')?.textContent?.trim() ||
        '';
    const description =
        content
            .querySelector('.ingredient-card__description')
            ?.textContent?.trim() || '';

    // Check if content is the same (prevent unnecessary updates)
    const currentTitle =
        this.contentElement
            .querySelector('.ingredient-card__title')
            ?.textContent?.trim() || '';

    if (currentTitle === title) {
      return;
    }

    // Cancel any pending transition
    if (this.contentUpdateTimeout) {
      clearTimeout(this.contentUpdateTimeout);
    }

    // Fade out
    this.contentElement.style.opacity = '0';

    // Update content after fade out
    this.contentUpdateTimeout = setTimeout(() => {
      this.contentElement.innerHTML = `
        <h4 class="ingredient-card__title">${title}</h4>
        <p class="ingredient-card__description">${description}</p>
      `;

      // Fade in
      requestAnimationFrame(() => {
        this.contentElement.style.opacity = '1';
      });
    }, 200);
  }

  /**
   * Public method to destroy carousel
   */
  destroyCarousel() {
    if (!this.initialized || !this.swiper) {
      return;
    }

    // Cancel all animation frames
    this.animationFrames.forEach((frameId) => {
      cancelAnimationFrame(frameId);
    });
    this.animationFrames.clear();

    // Clear content update timeout
    if (this.contentUpdateTimeout) {
      clearTimeout(this.contentUpdateTimeout);
      this.contentUpdateTimeout = null;
    }

    // Destroy Swiper instance
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.initialized = false;
  }

  disconnectedCallback() {
    // Use the public destroy method
    this.destroyCarousel();
  }

  /**
   * Public method to get Swiper instance
   * @returns {Object|null} Swiper instance
   */
  getSwiperInstance() {
    return this.swiper;
  }

  /**
   * Public method to slide to specific index
   * @param {number} index - Slide index
   */
  slideTo(index) {
    if (this.swiper) {
      this.swiper.slideTo(index);
    }
  }
}

// Register the custom elements
if ('customElements' in window) {
  customElements.define('ingredients-section', IngredientsSection);
  customElements.define('ingredients-carousel', IngredientsCarousel);
} else {
  console.error('Custom Elements are not supported in this browser');
}
